import React from 'react'
import { styled } from 'linaria/react'

const MultiCheckboxFilterListWrapper = styled('div')`
   dl,
   ol,
   ul {
      margin-bottom: 0rem;
      padding-bottom: 0.75rem;
   }

   // Overriding the bootstrap class form-check-input
   .form-check-input {
      cursor: pointer;
      border-color: #707070;
      margin-right: 0.5rem;
      box-shadow: none;
      outline: none;
      margin-left: 1px;
      &:focus {
         outline: 1px solid var(--color-vb-blue-darker);
         outline-offset: 0px;
      }
      &:hover {
         background-color: #dcdcdc;
      }
   }

   // Overriding the bootstrap class form-check-input
   .form-check-input:checked {
      background-color: var(--color-vb-blue);
      border-color: var(--color-vb-blue-darker);
      &:hover {
         opacity: 0.8;
      }
   }

   .filter-list {
      list-style-type: none;
      padding-left: 0;
   }

   .filter-label {
      white-space: nowrap;
      text-overflow: ellipsis;
      overflow: hidden;
      cursor: pointer;
      font-weight: 600;
   }

   .filter-item-wrapper {
      display: flex;
      gap: 8px;
      align-items: center;
      margin-bottom: 0.25rem;
   }

   .filter-count {
      margin-left: 0.5rem;
   }
`

const transformItems = items => {
   if (!items || !Array.isArray(items)) return []
   return items
      .filter(
         item =>
            item &&
            (typeof item.value === 'string' || typeof item.value === 'number') // Accept string or number
      )
      .map(item => ({
         ...item,
         count: item?.count ? `(${item.count})` : '', // Handle missing counts gracefully
         label: item?.value?.toString(), // Convert value to string for UI display
      }))
      .sort(
         (a, b) => a?.value?.toString().localeCompare(b?.value?.toString()) // Ensure consistent sorting
      )
}

/**
 *
 * A generic multi-checkbox filter list for displaying and interacting with filter options.
 *
 * @param {string} fieldName - The name of the field being filtered. Used to group filter values.
 * @param {Array} filters - Current active filters passed explicitly. These filters help determine if an option is selected.
 * @param {Array} options - The options to display in the checkbox list.
 *    Each option should have a `value` (string or number) and optionally a `count` (number).
 * @param {function} isSelected - A function that determines whether a specific option is selected.
 *    This function receives an object with `fieldName`, `value`, and `filters` properties.
 * @param {function} onChange - Callback function triggered when a checkbox is toggled.
 *    This function receives an object with `fieldName` and the toggled `value`.
 */
export const MultiCheckboxFilterList = ({
   fieldName,
   filters,
   options,
   isSelected,
   onChange,
}) => (
   <MultiCheckboxFilterListWrapper>
      <ul className="filter-list">
         {transformItems(options)?.map(item => {
            const inputId = `${fieldName}-${item.value}`
            return (
               <li key={item?.label} className="filter-item-wrapper">
                  <label className="filter-label" htmlFor={inputId}>
                     <input
                        id={inputId}
                        name={fieldName}
                        type="checkbox"
                        className="form-check-input"
                        checked={isSelected({
                           fieldName,
                           value: item?.value,
                           filters: Array.isArray(filters) ? filters : [],
                        })}
                        onChange={() => {
                           onChange?.({
                              fieldName,
                              value: item?.value,
                           })
                        }}
                     />
                     <span>{item?.label}</span>
                     <span className="filter-count">{item?.count}</span>
                  </label>
               </li>
            )
         })}
      </ul>
   </MultiCheckboxFilterListWrapper>
)
